import { memo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Eye, 
  Settings, 
  BarChart3, 
  Calendar, 
  X, 
  Lightbulb,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TooltipContextual } from "@/components/ayuda/TooltipContextual";

interface BannerVistaPropietarioProps {
  profileType: "trainer" | "nutritionist" | "physiotherapist" | "listing" | "business";
  profileId: string;
  profileSlug?: string;
  className?: string;
}

const PROFILE_CONFIG = {
  trainer: {
    editPath: "/edit-trainer-profile",
    dashboardPath: "/dashboard/reservas",
    analyticsPath: "/dashboard",
    label: "Entrenador",
  },
  nutritionist: {
    editPath: "/edit-nutritionist-profile",
    dashboardPath: "/dashboard/nutrition/reservas",
    analyticsPath: "/dashboard",
    label: "Nutricionista",
  },
  physiotherapist: {
    editPath: "/edit-physiotherapist-profile",
    dashboardPath: "/dashboard/physio/reservas",
    analyticsPath: "/dashboard",
    label: "Fisioterapeuta",
  },
  listing: {
    editPath: "/listing",
    dashboardPath: "/dashboard",
    analyticsPath: "/dashboard",
    label: "Listado",
  },
  business: {
    editPath: "/dashboard/business/edit",
    dashboardPath: "/dashboard/business/reservas",
    analyticsPath: "/dashboard",
    label: "Negocio",
  },
};

/**
 * BannerVistaPropietario - Banner que aparece cuando el propietario ve su propio perfil
 * 
 * Informa al usuario que está viendo su perfil como lo verían los visitantes
 * y proporciona accesos directos a edición, reservas y analytics.
 */
export const BannerVistaPropietario = memo(({
  profileType,
  profileId,
  profileSlug,
  className,
}: BannerVistaPropietarioProps) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [showTip, setShowTip] = useState(false);
  
  const config = PROFILE_CONFIG[profileType];
  const editPath = profileType === "listing" 
    ? `${config.editPath}/${profileId}/edit`
    : config.editPath;

  // Show first-time tip
  useEffect(() => {
    const tipKey = `owner-view-tip-${profileType}`;
    const hasSeenTip = localStorage.getItem(tipKey);
    if (!hasSeenTip) {
      setShowTip(true);
      localStorage.setItem(tipKey, "true");
      // Auto-hide after 8 seconds
      const timer = setTimeout(() => setShowTip(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [profileType]);

  if (isDismissed) return null;

  return (
    <div className={cn(
      "sticky top-16 z-40 animate-in slide-in-from-top duration-300",
      className
    )}>
      {/* First-time tip tooltip */}
      {showTip && (
        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2">
          <div className="bg-amber-50 dark:bg-amber-950/80 border border-amber-200 dark:border-amber-800 rounded-lg p-3 shadow-lg max-w-xs">
            <div className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-200">
                <strong>¡Consejo!</strong> Así es como los visitantes ven tu perfil. 
                Usa los botones de arriba para editar o gestionar.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-primary/20">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {/* Left: Status info */}
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30">
                <Eye className="h-3 w-3 mr-1" />
                Vista de Propietario
              </Badge>
              <span className="text-sm text-muted-foreground hidden sm:inline">
                Estás viendo tu perfil de {config.label.toLowerCase()} como lo ven los visitantes
              </span>
              <TooltipContextual 
                content={
                  <div className="space-y-2">
                    <p className="font-medium">Vista de Propietario</p>
                    <p className="text-muted-foreground text-xs">
                      Esta vista te muestra exactamente cómo aparece tu perfil para 
                      los clientes potenciales. Puedes editar tu información o 
                      gestionar tus reservas usando los botones de acceso rápido.
                    </p>
                  </div>
                }
                side="bottom"
              />
            </div>

            {/* Right: Quick actions */}
            <div className="flex items-center gap-2">
              <Link to={editPath}>
                <Button size="sm" variant="outline" className="h-8 text-xs">
                  <Settings className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">Editar Perfil</span>
                  <span className="sm:hidden">Editar</span>
                </Button>
              </Link>
              <Link to={config.dashboardPath}>
                <Button size="sm" variant="outline" className="h-8 text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">Mis Reservas</span>
                  <span className="sm:hidden">Reservas</span>
                </Button>
              </Link>
              <Link to={config.analyticsPath}>
                <Button size="sm" variant="outline" className="h-8 text-xs hidden md:flex">
                  <BarChart3 className="h-3 w-3 mr-1" />
                  Analytics
                </Button>
              </Link>
              
              <button
                onClick={() => setIsDismissed(true)}
                className="p-1.5 hover:bg-muted rounded-full transition-colors text-muted-foreground hover:text-foreground ml-1"
                aria-label="Cerrar banner"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

BannerVistaPropietario.displayName = "BannerVistaPropietario";
